import {app} from '../../app';
import IScope = angular.IScope;
import {SharedQuestionService} from "../../services/SharedQuestionService";
import {ISessionService} from "../../services/SessionService";
import IComponentController = angular.IComponentController;
import {DragAndDropSharedQuestion} from "../../sharedQuestions/DragAndDropSharedQuestion";
import {IUniqueIdService} from "../../services/UniqueIdService";

/**
 *
 *
 * Note:
 *  Supports Accessible Drag and Drop Using WAI-ARIA
 *  using https://dev.opera.com/articles/accessible-drag-and-drop/
 */
export class DragAndDropQuestionComponent {

    protected timeout:ng.ITimeoutService;
    protected sharedQuestionService:SharedQuestionService;
    protected sessionService:ISessionService;
    protected uniqueId:IUniqueIdService;

    private popoverTimeout:ng.IPromise<any>;

    sharedQuestion:DragAndDropSharedQuestion;

    nextQuestionCallback:()=>void;
    question:IQuestion;
    values:{[s:string]:{label:string, ariaMenuOpen:boolean}[]} = {};
    containers:IDragAndDropContainer[];
    id:string;

    static $inject = ['$timeout', 'SharedQuestionService', 'SessionService', 'UniqueIdService'];

    constructor($timeout:ng.ITimeoutService, sharedQuestionService:SharedQuestionService, sessionService:ISessionService, uniqueId:IUniqueIdService) {
        this.timeout               = $timeout;
        this.sharedQuestionService = sharedQuestionService;
        this.sessionService        = sessionService;
        this.uniqueId              = uniqueId;
    }

    public $onInit = () => {
        this.sharedQuestion = new DragAndDropSharedQuestion();

        this.setup();
    };

    setup() {
        this.sharedQuestion.question = this.question;

        this.sharedQuestionService.questions.push(this.sharedQuestion);
        this.sharedQuestion.questionDisplayed();

        this.id = this.uniqueId.getUniqueId('drag-and-drop');

        this.containers = this.question.meta.containers;
        for (const container of this.containers) {
            let elements = [];
            for (const element of container.elements) {
                elements.push({label: element, ariaMenuOpen: false});
            }
            this.values[container.title] = elements;
        }

        let answer = this.sessionService.getAnswer(this.question.id);
        if (answer && angular.isArray(answer.values)) {
            for (let valueIndex = 0; valueIndex < answer.values.length; valueIndex++) {
                const value                                         = answer.values[valueIndex].value;
                let elements:{label:string, ariaMenuOpen:boolean}[] = [];
                for (const element of value) {
                    elements.push({label: element, ariaMenuOpen: false});
                }
                this.values[this.containers[valueIndex].title] = elements;
            }

            this.update();
        }

        this.update();
    }

    /**
     * Fires when draggable element is about to be moved.
     * It prepares aria fields for it.
     *
     * @param event
     */
    ariaDragStart(event) {
        const element = angular.element(event.srcElement);

        element.attr('aria-grabbed', 'true');
        element.attr('aria-dropeffect', 'move');
    }

    /**
     * Fires on key down on draggable element.
     * If the key is space it opens aria compatible popup with a list of possible containers,
     * to which the draggable element can be moved.
     *
     * @param elementIndex
     * @param containerTitle
     * @param event
     */
    ariaKeyDown(elementIndex, containerTitle, event) {
        const keyCode = event.keyCode,
              element = angular.element(event.target || event.srcElement),
              value   = this.values[containerTitle][elementIndex];

        if (keyCode == 32) {
            event.preventDefault();

            value.ariaMenuOpen = true;

            element.attr('aria-grabbed', 'true');
            element.attr('aria-owns', 'popup');

            this.timeout(100).then(() => {
                element.children('ul').first().children('li').first().focus();
            });
        }
    }

    /**
     * Fires on key down on a containers popup list item displayed by ariaKeyDown() method.
     * Allows to navigates through the list using keys like: up, down, enter (to accept),
     * esc (to close), and tab (to close and go to next draggable element).
     *
     * @param elementIndex
     * @param containerTitle
     * @param newContainerTitle
     * @param event
     */
    ariaMenuKeyDown(elementIndex, containerTitle, newContainerTitle, event) {
        const keyCode            = event.keyCode,
              element            = angular.element(event.target || event.srcElement);

        if (event.stopPropagation) {
            event.stopPropagation();
        } else if (event.cancelBubble) {
            event.cancelBubble = true;
        }

        if (event.preventDefault) {
            event.preventDefault();
        } else if (event.returnValue) {
            event.returnValue = false;
        }

        if (!this.values[containerTitle][elementIndex].ariaMenuOpen) {
            this.ariaFocusNextDraggable(element);
        }

        switch (keyCode) {
            case 40: // down arrow
            {
                let nextElement = element.next('li');
                if (nextElement.length <= 0) {
                    nextElement = element.parent().children('li').first()
                }

                nextElement.focus();
                // nextElement.className = 'focus';
                break;
            }
            case 38: // up arrow
            {
                let nextElement = element.prev('li');
                if (nextElement.length <= 0) {
                    nextElement = element.parent().children('li').last()
                }

                nextElement.focus();
                // nextElement.className = 'focus';
                break;
            }
            case 13: // enter
                this.closeAllAriaMenus();
                this.values[newContainerTitle].push(
                    this.values[containerTitle].splice(elementIndex, 1)[0]
                );
                this.update();
                this.ariaFocusNextDraggable(element);
                break;
            case 27: // escape
                this.closeAllAriaMenus();
                this.ariaFocusNextDraggable(element);
                break;
            case 9: // tab
                this.closeAllAriaMenus();
                this.ariaFocusNextDraggable(element);
                break;
        }
    }

    /**
     * Fires on a mouse down, to accept the container from a containers popup list
     *
     * @param elementIndex
     * @param containerTitle
     * @param newContainerTitle
     * @param event
     */
    ariaMenuMouseDown(elementIndex, containerTitle, newContainerTitle, event) {
        const keyCode            = event.keyCode,
              element            = angular.element(event.target || event.srcElement);

        this.closeAllAriaMenus();

        this.values[newContainerTitle].push(
            this.values[containerTitle].splice(elementIndex, 1)[0]
        );
        this.update();

        this.ariaFocusNextDraggable(element);
    }

    ariaFocusNextDraggable(element: JQuery) {
        this.timeout(100).then(() => {
            const next = element.parent().closest('li').next('li');
            if (next) {
                next.focus();
            }
        });
    }

    /**
     * Closes all visible containers lists popups
     */
    closeAllAriaMenus() {
        for (const title in this.values) {
            if (!this.values.hasOwnProperty(title)) {
                continue;
            }

            for (const element of this.values[title]) {
                element.ariaMenuOpen = false;
            }
        }
    }

    update() {
        if (this.question.meta.ordered === false) {
            this.sortValues();
        }

        let values:IValue[] = [];
        for (const title in this.values) {
            if (!this.values.hasOwnProperty(title)) {
                continue;
            }

            const container  = this.values[title];
            let value:IValue = {value: []};
            for (const element of container) {
                value.value.push(element.label);
            }
            values.push(value);
        }
        this.sharedQuestion.setValue(values);
    }

    private sortValues() {
        let index = 0;
        for (const title in this.values) {
            if (!this.values.hasOwnProperty(title)) {
                continue;
            }

            let valuesCopy = [];
            angular.copy(this.values[title], valuesCopy);

            this.values[title].sort((a, b) => {
                if (a.label < b.label) return -1;
                if (a.label > b.label) return 1;
                return 0;
            });

            let valueIndex = 0;
            for (const value of this.values[title]) {
                const valueCopy = valuesCopy[valueIndex];

                if (value.label !== valueCopy.label) {
                    const element = angular.element('#' + this.id);

                    this.timeout.cancel(this.popoverTimeout);

                    element.popover({
                        content  : 'Order doesn\'t matter in this question!',
                        placement: 'top'
                    }).popover('show');

                    // hide popup after some time
                    this.popoverTimeout = this.timeout(() => {
                        element.popover('hide');
                    }, 3000);
                }

                valueIndex++;
            }

            index++;
        }
    }

}

app.component('dragAndDropQuestion', {
    templateUrl : 'templates/components/questionTypes/dragAndDropQuestion.html',
    controller  : DragAndDropQuestionComponent,
    controllerAs: 'dragAndDropQuestion',
    bindings    : {
        question            : '<',
        nextQuestionCallback: '&'
    }
});